import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
  Breadcrumbs,
  Button,
  FormControl,
  Grid,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { DateRangePicker } from "@material-ui/pickers";
import * as C from "components";
import {
  setCurrentPage,
  getPicTreeRegularSeasonEventPageData,
  getPicTreeRegularSeasonEventBootstrapData,
} from "actions";
import SeasonSelector from "components/PicTree/RegularSeason/SeasonSelector";
import RegularSeasonKpiEventCards from "components/PicTree/RegularSeason/RegularSeasonKpiEventCards";
import RegularSeasonDailyTrendChart from "components/PicTree/RegularSeason/RegularSeasonDailyTrendChart";
import RegularSeasonEventTable from "components/PicTree/RegularSeason/RegularSeasonEventTable";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  breadcrumbs: {
    marginTop: 16,
    minHeight: 30,
    width: "100%",
    justifyContent: "flex-start",
  },
  topBar: {
    marginTop: -16,
    marginBottom: 8,
  },
  filterRow: {
    paddingTop: theme.spacing(1),
    width: "100%",
    display: "flex",
    justifyContent: "flex-end",
  },
  sectionGap: {
    marginTop: theme.spacing(2),
  },
  filters: {
    display: "flex",
    justifyContent: "flex-end",
    overflowX: "auto",
  },
  eventSelectWrap: {
    minWidth: 230,
    maxWidth: 320,
    width: "100%",
  },
  eventSelect: {
    "& .MuiSelect-select": {
      minHeight: 20,
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
    },
  },
  dateRangeWrap: {
    display: "flex",
    alignItems: "center",
    width: 276,
    marginTop: 0,
  },
  dateRangeInput: {
    display: "flex",
    alignItems: "baseline",
  },
  dateField: {
    width: 120,
  },
  dateDelimiter: {
    margin: "0 4px",
    color: "#556070",
  },
  goButton: {
    height: 32,
    minWidth: 60,
    paddingLeft: 18,
    paddingRight: 18,
    color: "#fff",
    fontSize: 15,
  },
}));

const ALL_BOOST_EVENTS_VALUE = "__ALL__";

const PicTreeRegularSeasonEventOverview = ({
  setCurrentPage,
  getPicTreeRegularSeasonEventPageData,
  getPicTreeRegularSeasonEventBootstrapData,
  selectedSeasonId = "cmunxinty000079zc6zf595vs",
  seasonOptions = [],
  companyOptions = [],
  selectedCompanyCode = "NTT_ME",
  selectedBoostEventId = ALL_BOOST_EVENTS_VALUE,
  selectedDateRange = [null, null],
  kpis = [],
  trend = {
    date: [],
    uniqueShots: [],
    reviewerOk: [],
    uniqueShooters: [],
  },
  trendLoading = false,
  trendRows = [],
  trendRowsLoading = false,
  eventRows = [],
  eventRowsLoading = false,
  bootstrapEventReady = false,
}) => {
  const classes = useStyles();
  const [currentSeasonIds, setCurrentSeasonIds] = useState([selectedSeasonId]);
  const [currentBoostEventId, setCurrentBoostEventId] = useState(ALL_BOOST_EVENTS_VALUE);
  const [hasManualEventSelection, setHasManualEventSelection] = useState(false);
  const [draftDateRange, setDraftDateRange] = useState([null, null]);
  const [initialDataCall, setInitialDataCall] = useState(true);
  const todayJst = moment().tz("Asia/Tokyo").format("YYYY-MM-DD");
  const didAutoSelectInitialEventRef = useRef(false);
  const requestedBootstrapSeasonIdRef = useRef(null);
  const draftDateRangeRef = useRef(draftDateRange);

  useEffect(() => {
    draftDateRangeRef.current = draftDateRange;
  }, [draftDateRange]);

  const requestInitialEventData = (nextSeasonId) => {
    if (!nextSeasonId || requestedBootstrapSeasonIdRef.current === nextSeasonId) {
      return;
    }

    requestedBootstrapSeasonIdRef.current = nextSeasonId;
    getPicTreeRegularSeasonEventBootstrapData({
      selectedSeasonId: nextSeasonId,
      selectedSeasonIds: [nextSeasonId],
    });
  };

  useEffect(() => {
    setCurrentPage(window.location.pathname);
    requestInitialEventData(selectedSeasonId);
  }, [getPicTreeRegularSeasonEventBootstrapData, selectedSeasonId, setCurrentPage]);

  useEffect(() => {
    didAutoSelectInitialEventRef.current = false;
  }, [selectedSeasonId]);

  useEffect(() => {
    if (!Array.isArray(selectedDateRange) || selectedDateRange.length < 2) {
      return;
    }

    const [startDate, endDate] = selectedDateRange;
    if (!startDate || !endDate) {
      return;
    }

    const currentStartDate = draftDateRangeRef.current?.[0]?.format?.("YYYY-MM-DD");
    const currentEndDate = draftDateRangeRef.current?.[1]?.format?.("YYYY-MM-DD");
    if (currentStartDate === startDate && currentEndDate === endDate) {
      return;
    }

    setDraftDateRange([
      moment(startDate, "YYYY-MM-DD"),
      moment(endDate, "YYYY-MM-DD"),
    ]);
  }, [selectedDateRange]);

  const handleSeasonChange = (event) => {
    const nextSeasonId = Array.isArray(event.target.value) ? event.target.value[0] : event.target.value;
    setCurrentSeasonIds(nextSeasonId ? [nextSeasonId] : []);
    setCurrentBoostEventId(ALL_BOOST_EVENTS_VALUE);
    setHasManualEventSelection(false);
    setDraftDateRange([null, null]);
    didAutoSelectInitialEventRef.current = false;
    setInitialDataCall(true);
    if (nextSeasonId) {
      requestInitialEventData(nextSeasonId);
    }
  };

  const eventOptions = useMemo(() => {
    const rows = Array.isArray(eventRows) ? eventRows : [];
    const uniqueMap = new Map();
    rows.forEach((row) => {
      if (!row?.id || uniqueMap.has(row.id)) {
        return;
      }
      uniqueMap.set(row.id, row);
    });

    return Array.from(uniqueMap.values()).map((row) => ({
      value: row.id,
      label: row.name || row.id,
      period: row.period || "",
    }));
  }, [eventRows]);

  useEffect(() => {
    if (!currentBoostEventId || currentBoostEventId === ALL_BOOST_EVENTS_VALUE) {
      return;
    }

    const hasCurrentOption = eventOptions.some((option) => option.value === currentBoostEventId);
    if (!hasCurrentOption) {
      setCurrentBoostEventId(ALL_BOOST_EVENTS_VALUE);
    }
  }, [currentBoostEventId, eventOptions]);

  useEffect(() => {
    if (!selectedBoostEventId || selectedBoostEventId === ALL_BOOST_EVENTS_VALUE) {
      return;
    }
    if (!Array.isArray(selectedDateRange) || selectedDateRange.length < 2) {
      return;
    }

    const [startDate, endDate] = selectedDateRange;
    if (!startDate || !endDate) {
      return;
    }

    if (didAutoSelectInitialEventRef.current && currentBoostEventId === selectedBoostEventId) {
      return;
    }

    didAutoSelectInitialEventRef.current = true;
    setCurrentBoostEventId(selectedBoostEventId);
    setHasManualEventSelection(false);
    setDraftDateRange([
      moment(startDate, "YYYY-MM-DD"),
      moment(endDate, "YYYY-MM-DD"),
    ]);
  }, [
    currentSeasonIds,
    currentBoostEventId,
    selectedBoostEventId,
    selectedDateRange,
    selectedSeasonId,
  ]);

  useEffect(() => {
    if (!initialDataCall) {
      return;
    }
    if (!bootstrapEventReady) {
      return;
    }
    if (!selectedBoostEventId || selectedBoostEventId === ALL_BOOST_EVENTS_VALUE) {
      return;
    }
    if (!Array.isArray(selectedDateRange) || selectedDateRange.length < 2) {
      return;
    }

    const [startDate, endDate] = selectedDateRange;
    if (!startDate || !endDate) {
      return;
    }

    getPicTreeRegularSeasonEventPageData({
      selectedSeasonId: currentSeasonIds[0] || selectedSeasonId,
      selectedSeasonIds: currentSeasonIds,
      selectedBoostEventId,
      selectedDateRange: [startDate, endDate],
      eventRows,
    });
    setInitialDataCall(false);
  }, [
    bootstrapEventReady,
    currentSeasonIds,
    getPicTreeRegularSeasonEventPageData,
    initialDataCall,
    selectedBoostEventId,
    selectedDateRange,
    selectedSeasonId,
  ]);

  const handleBoostEventChange = (event) => {
    setHasManualEventSelection(true);
    setCurrentBoostEventId(event.target.value || ALL_BOOST_EVENTS_VALUE);
  };

  const handleDateRangeChange = (nextValue) => {
    setDraftDateRange(nextValue || [null, null]);
  };

  const handleGoClick = () => {
    if (!effectiveBoostEventId || effectiveBoostEventId === ALL_BOOST_EVENTS_VALUE) {
      return;
    }

    const nextDateRange = Array.isArray(effectiveDateRange) && effectiveDateRange.length === 2
      ? effectiveDateRange
      : [null, null];
    const hasRange = nextDateRange[0] && nextDateRange[1];

    getPicTreeRegularSeasonEventPageData({
      selectedSeasonId: currentSeasonIds[0] || selectedSeasonId,
      selectedSeasonIds: currentSeasonIds,
      selectedBoostEventId: effectiveBoostEventId,
      eventRows,
      selectedDateRange: hasRange
        ? [
          moment(nextDateRange[0]).format("YYYY-MM-DD"),
          moment(nextDateRange[1]).format("YYYY-MM-DD"),
        ]
        : undefined,
    });
  };

  const selectedCompany = companyOptions.find((item) => item.value === selectedCompanyCode);
  const effectiveBoostEventId = hasManualEventSelection
    ? currentBoostEventId
    : (selectedBoostEventId && selectedBoostEventId !== ALL_BOOST_EVENTS_VALUE
      ? selectedBoostEventId
      : currentBoostEventId);
  const effectiveDateRange = (Array.isArray(draftDateRange) && draftDateRange[0] && draftDateRange[1])
    ? draftDateRange
    : (
      Array.isArray(selectedDateRange) && selectedDateRange.length >= 2 && selectedDateRange[0] && selectedDateRange[1]
        ? [moment(selectedDateRange[0], "YYYY-MM-DD"), moment(selectedDateRange[1], "YYYY-MM-DD")]
        : [null, null]
    );
  const selectedEventOption = eventOptions.find((option) => option.value === effectiveBoostEventId);
  const selectBoostEventId = selectedEventOption ? effectiveBoostEventId : "";
  const selectedEventLabel = selectedEventOption?.label || "";

  return (
    <Fragment>
      <C.ContentWrapper>
        <Grid
          container
          spacing={1}
          direction="row"
          wrap="wrap"
          className={classes.topBar}
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Grid item xs={12} md={4}>
            <Breadcrumbs separator="›" className={classes.breadcrumbs}>
              <Typography>PicTrée 全体</Typography>
              <Typography variant="body1">レギュラーシーズン</Typography>
              <Typography variant="body1">イベント</Typography>
              <Typography variant="body1">概要</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid item xs={12} md={8} className={classes.filterRow}>
            <Grid container spacing={1} alignItems="center" justifyContent="flex-end" className={classes.filters}>
              <Grid item>
                <SeasonSelector
                  selectedSeasonIds={currentSeasonIds}
                  seasonOptions={seasonOptions}
                  selectedCompanyLabel={selectedCompany ? selectedCompany.label : "NTT ME"}
                  onSeasonChange={handleSeasonChange}
                  showCheckboxes={false}
                  allowMultiple={false}
                  selectMinWidth={220}
                  selectMaxWidth={300}
                />
              </Grid>
              <Grid item className={classes.eventSelectWrap}>
                <FormControl variant="outlined" fullWidth size="small">
                  <Select
                    displayEmpty
                    className={classes.eventSelect}
                    value={selectBoostEventId}
                    onChange={handleBoostEventChange}
                    disabled={eventRowsLoading}
                    renderValue={(selected) => {
                      const option = eventOptions.find((item) => item.value === selected);
                      return option ? option.label : selectedEventLabel;
                    }}
                  >
                    {eventOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <ListItemText
                          primary={option.label}
                          secondary={option.period || undefined}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item className={classes.dateRangeWrap}>
                <DateRangePicker
                  startText="Start"
                  endText="End"
                  disableHighlightToday
                  showToolbar={false}
                  allowSameDateSelection
                  maxDate={todayJst}
                  value={effectiveDateRange}
                  onChange={handleDateRangeChange}
                  renderInput={(startProps, endProps) => (
                    <div className={classes.dateRangeInput}>
                      <TextField
                        {...startProps}
                        size="small"
                        className={classes.dateField}
                        helperText=""
                        InputLabelProps={{
                          ...(startProps?.InputLabelProps || {}),
                          shrink: true,
                        }}
                      />
                      <span className={classes.dateDelimiter}>~</span>
                      <TextField
                        {...endProps}
                        size="small"
                        className={classes.dateField}
                        helperText=""
                        InputLabelProps={{
                          ...(endProps?.InputLabelProps || {}),
                          shrink: true,
                        }}
                      />
                    </div>
                  )}
                />
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.goButton}
                  disableElevation
                  onClick={handleGoClick}
                >
                  GO
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid container spacing={1} className={classes.sectionGap}>
          <Grid item xs={12}>
            <RegularSeasonKpiEventCards kpis={kpis} />
          </Grid>
        </Grid>

        <Grid container spacing={1} className={classes.sectionGap}>
          <Grid item xs={12}>
            <RegularSeasonDailyTrendChart trend={trend} loading={trendLoading} />
          </Grid>
        </Grid>

        <Grid container spacing={1} className={classes.sectionGap}>
          <Grid item xs={12}>
            <RegularSeasonEventTable rows={trendRows} loading={trendRowsLoading} />
          </Grid>
        </Grid>
      </C.ContentWrapper>
    </Fragment>
  );
};

PicTreeRegularSeasonEventOverview.propTypes = {
  setCurrentPage: PropTypes.func,
  getPicTreeRegularSeasonEventPageData: PropTypes.func,
  getPicTreeRegularSeasonEventBootstrapData: PropTypes.func,
  selectedSeasonId: PropTypes.string,
  selectedSeasonIds: PropTypes.array,
  selectedBoostEventId: PropTypes.string,
  selectedDateRange: PropTypes.array,
  seasonOptions: PropTypes.array,
  companyOptions: PropTypes.array,
  selectedCompanyCode: PropTypes.string,
  kpis: PropTypes.array,
  trend: PropTypes.object,
  trendLoading: PropTypes.bool,
  trendRows: PropTypes.array,
  trendRowsLoading: PropTypes.bool,
  eventRows: PropTypes.array,
  eventRowsLoading: PropTypes.bool,
  bootstrapEventReady: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  loading: state.pictreeRegularSeasonEvent.loading,
  selectedSeasonId: state.pictreeRegularSeasonEvent.selectedSeasonId,
  selectedSeasonIds: state.pictreeRegularSeasonEvent.selectedSeasonIds,
  selectedBoostEventId: state.pictreeRegularSeasonEvent.selectedBoostEventId,
  selectedCompanyCode: state.pictreeRegularSeasonEvent.selectedCompanyCode,
  seasonOptions: state.pictreeRegularSeasonEvent.seasonOptions,
  companyOptions: state.pictreeRegularSeasonEvent.companyOptions,
  kpis: state.pictreeRegularSeasonEvent.kpis,
  trend: state.pictreeRegularSeasonEvent.trend,
  trendLoading: state.pictreeRegularSeasonEvent.trendLoading,
  trendRows: state.pictreeRegularSeasonEvent.trendRows,
  trendRowsLoading: state.pictreeRegularSeasonEvent.trendRowsLoading,
  eventRows: state.pictreeRegularSeasonEvent.eventRows,
  eventRowsLoading: state.pictreeRegularSeasonEvent.eventRowsLoading,
  selectedDateRange: state.pictreeRegularSeasonEvent.selectedDateRange,
  bootstrapEventReady: state.pictreeRegularSeasonEvent.bootstrapEventReady,
});

const mapDispatchToProps = (dispatch) => ({
  setCurrentPage: (param) => dispatch(setCurrentPage(param)),
  getPicTreeRegularSeasonEventPageData: (param) => dispatch(getPicTreeRegularSeasonEventPageData(param)),
  getPicTreeRegularSeasonEventBootstrapData: (param) => dispatch(getPicTreeRegularSeasonEventBootstrapData(param)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PicTreeRegularSeasonEventOverview);
